/**
 * **zenith 乐谱：给 `BgmDirector` 的数据。**
 *
 * 这个文件里才有游戏参数（`isFinal` / `isDying` / `energy`）。引擎不认识它们 ——
 * 引擎只认「一组参数 + 一张规则表」。要换曲子、加参数、改规则，改这里。
 *
 * ## 数据来源
 *
 * 从 TETR.IO Quick Play（Zenith）模式的动态 BGM 逆向而来：
 * `https://tetr.io/res/bgm/zenith.rcd`（`tRCD` v1.0，7,339 字节）。
 * 完整的格式规范、段落图、参数语义见 `docs/zenith/README.md`。
 *
 * ## ⚠️ 这两个 m4a 是**音频精灵图**，不是完整的歌
 *
 * 原版把很多段音频拼在一个文件里，靠 `.rcd` 里的偏移量按需取用，`P0`–`P12` 就是索引。
 * **不要整段播放** —— 线性听会在 1:54 等处听到生硬的拼接，那是精灵图里没人播放的
 * 「零件区」，不是 bug。每首曲子只用 `loop` 指定的那一段。
 *
 * | 音轨 | 引入段 | 循环区间 | 结尾 |
 * |---|---|---|---|
 * | `floor1` | `[0, 10.435]` (8 小节) | `[10.435, 114.783]` (80 小节) | — |
 * | `floor9` | `[0, 36.0]` (24 小节) | `[36.0, 144.0]` (72 小节) | `[144.0, 147.0]` 收尾 |
 *
 * ## 只有两个 cue
 *
 * 正位 / 逆位**不是两个 cue，而是同一个 cue 里的层**：
 *
 * - 大厅逆位 = **条件叠加层**（floor1 继续播，floor1-reverse 叠上来）
 * - 第九层逆位 = **互斥变体**（floor9 换成 floor9-reverse）
 *
 * 这样切变体只改增益，**不重播、不丢播放位置** —— 否则每次 `isDying` 都会
 * 从头开始，这正是「isDying 没有首次」那个现象的根因。
 */

import type {
  AudioSource,
  BgmScore,
  CueRule,
  CueSpec,
  ParamBag,
  RandomLayerSpec,
  TrackSpec,
} from './BgmDirector';

import floor1M4a from './assets/zenith/floor1.m4a';
import floor1Ogg from './assets/zenith/floor1.ogg';
import floor1ReverseM4a from './assets/zenith/floor1-reverse.m4a';
import floor1ReverseOgg from './assets/zenith/floor1-reverse.ogg';
import floor9M4a from './assets/zenith/floor9.m4a';
import floor9Ogg from './assets/zenith/floor9.ogg';
import floor9ReverseM4a from './assets/zenith/floor9-reverse.m4a';
import floor9ReverseOgg from './assets/zenith/floor9-reverse.ogg';
import specArpM4a from './assets/zenith/spec-arp.m4a';
import specArpOgg from './assets/zenith/spec-arp.ogg';
import specBassM4a from './assets/zenith/spec-bass.m4a';
import specBassOgg from './assets/zenith/spec-bass.ogg';
import specGuitarM4a from './assets/zenith/spec-guitar.m4a';
import specGuitarOgg from './assets/zenith/spec-guitar.ogg';
import specPadM4a from './assets/zenith/spec-pad.m4a';
import specPadOgg from './assets/zenith/spec-pad.ogg';
import specStaccatoM4a from './assets/zenith/spec-staccato.m4a';
import specStaccatoOgg from './assets/zenith/spec-staccato.ogg';
import specViolinM4a from './assets/zenith/spec-violin.m4a';
import specViolinOgg from './assets/zenith/spec-violin.ogg';

// ------------------------------------------------------------ 参数名 ----

/**
 * 游戏侧的参数名。**这是游戏的概念，不是引擎的**。
 * 集中在这里是为了「规则表」和「调用方」不会各写一遍字符串写岔。
 */
export const ZENITH_PARAM = {
  /** 是否进入终局：false = 大厅系，true = 第九层系 */
  isFinal: 'isFinal',
  /** 是否濒死：决定正位 / 逆位；且**首次**为真时把音乐拉回循环开头 */
  isDying: 'isDying',
  /**
   * 气氛强度，0–6 整数。**不参与选曲**，只决定大厅随机装饰层同时开几层。
   * 每层都是从 6 个 `spec-*` 装饰层里随机抓的，抓取数量 =
   * `[floor(energy/2), energy]` 区间内的随机整数。
   */
  energy: 'energy',
} as const;

/** `energy` 的取值上限。 */
export const ENERGY_MAX = 6;

/** 游戏要喂给指挥器的参数。 */
export interface ZenithBgmParams {
  isFinal: boolean;
  isDying: boolean;
  /** 气氛强度，0–6 整数。只影响大厅的随机装饰层数量。 */
  energy: number;
}

/** 把游戏参数摊成引擎要的 `ParamBag`。 */
export function zenithParams(p: ZenithBgmParams): ParamBag {
  return {
    [ZENITH_PARAM.isFinal]: p.isFinal,
    [ZENITH_PARAM.isDying]: p.isDying,
    [ZENITH_PARAM.energy]: p.energy,
  };
}

// -------------------------------------------------------------- 音轨 ----

/** AAC / M4A。Safari、Chrome、macOS 都稳。 */
const AAC = 'audio/mp4; codecs="mp4a.40.2"';
/** Opus / Ogg。Chrome 系和 Firefox 自带。**Linux 上没有 AAC 的浏览器靠它兜底。** */
const OPUS = 'audio/ogg; codecs="opus"';

/**
 * 同一首曲子的两种封装，按顺序回退。
 *
 * 为什么不能只给 m4a：Linux 上的 Chromium / Brave / ungoogled-chromium
 * 默认**不带 AAC**，`decodeAudioData` 会直接抛
 * `EncodingError: Unable to decode audio data`。
 * 这不是文件坏了 —— `curl` 下来字节完全一致，就是解不了。
 */
const src = (m4a: string, ogg: string): readonly AudioSource[] => [
  { url: m4a, mime: AAC },
  { url: ogg, mime: OPUS },
];

/** 大厅的 6 个装饰层。原版 `.rcd` 的 `spec-*.m4a`（随机池）。 */
const SPEC_POOL = [
  'specArp',
  'specBass',
  'specGuitar',
  'specPad',
  'specStaccato',
  'specViolin',
] as const;

const tracks: Record<string, TrackSpec> = {
  lobby: { sources: src(floor1M4a, floor1Ogg), bpm: 184, beatsPerBar: 4 },
  lobbyReverse: { sources: src(floor1ReverseM4a, floor1ReverseOgg), bpm: 184, beatsPerBar: 4 },
  final: { sources: src(floor9M4a, floor9Ogg), bpm: 160, beatsPerBar: 4 },
  finalReverse: { sources: src(floor9ReverseM4a, floor9ReverseOgg), bpm: 160, beatsPerBar: 4 },
  // 装饰层和主轨同速（184bpm），因为它们和 floor1 是同一首曲子的分层
  specArp: { sources: src(specArpM4a, specArpOgg), bpm: 184, beatsPerBar: 4 },
  specBass: { sources: src(specBassM4a, specBassOgg), bpm: 184, beatsPerBar: 4 },
  specGuitar: { sources: src(specGuitarM4a, specGuitarOgg), bpm: 184, beatsPerBar: 4 },
  specPad: { sources: src(specPadM4a, specPadOgg), bpm: 184, beatsPerBar: 4 },
  specStaccato: { sources: src(specStaccatoM4a, specStaccatoOgg), bpm: 184, beatsPerBar: 4 },
  specViolin: { sources: src(specViolinM4a, specViolinOgg), bpm: 184, beatsPerBar: 4 },
};

// -------------------------------------------------------------- 区间 ----

/**
 * 大厅循环区间：floor1 的第 8 小节到第 88 小节，共 80 小节。
 *
 * ⚠️ `10.435` / `114.783` 是 `.rcd` 里的原值，**按毫秒取整**过。
 * 80 小节的真值是 `80 * 4*60/184 = 104.347826…s`，而区间长度是 `104.348s`，
 * 差 0.17ms。只在连续停留同一 cue 时累积（换 cue / 重启都会重锚时钟），
 * 一小时约 6ms。`test/audio/score.test.ts` 盯着这个不变量。
 */
const LOBBY_LOOP = { start: 10.435, end: 114.783 };

/**
 * 大厅引入段起点。`.rcd` 的 `P0 [0, 10.435]`，只播一次。
 * 它和 `LOBBY_LOOP` 在文件里连续（`intro.end === loop.start`），
 * 所以只要从这里起步，第一遍播到 `loop.end` 之后自然回到循环。
 */
const LOBBY_INTRO = 0;

/** 第九层循环区间：floor9 的第 24 小节到第 96 小节，共 72 小节。 */
const FINAL_LOOP = { start: 36.0, end: 144.0 };

/** 第九层引入段起点。`.rcd` 的 `P49 [0, 36.0]`，**24 小节的前奏**，只播一次。 */
const FINAL_INTRO = 0;

/** 第九层收尾段。`.rcd` 的 `P51 [144.0, 147.0]`，3 秒，离开终局时播一次。 */
const FINAL_OUTRO = { start: 144.0, end: 147.0 };

// ---------------------------------------------------------------- cue ----

/**
 * 大厅随机装饰层。
 *
 * 数量由 `energy` 决定：在 `[floor(energy/2), energy]` 里取随机整数。
 * - `energy = 0` → 0 层（只有主轨）
 * - `energy = 3` → 1–3 层
 * - `energy = 6` → 3–6 层
 *
 * 重掷间隔 20–60 秒、单层淡入 6 秒，照抄原版 `.rcd` 的 `randoms` 参数。
 */
const SPEC_RANDOM: RandomLayerSpec = {
  pool: SPEC_POOL,
  count: (params) => {
    const raw = Number(params[ZENITH_PARAM.energy] ?? 0);
    const energy = Number.isFinite(raw) ? Math.min(Math.max(Math.round(raw), 0), ENERGY_MAX) : 0;
    return { min: Math.floor(energy / 2), max: energy };
  },
  reroll: [20, 60],
  fade: 6,
};

const cues: Record<string, CueSpec> = {
  /**
   * 大厅。**逆位是叠加层不是换 cue** ——
   * 原版 `.rcd` 的 dynLayer 18 就是这个行为：floor1 继续播，
   * floor1-reverse 叠上来（实测两者时长相同、内容相关性 ≈ 0，
   * 是两份对齐的编曲，设计上同时播放）。
   */
  lobby: {
    track: 'lobby',
    loop: LOBBY_LOOP,
    playFrom: LOBBY_INTRO,
    layers: [{ track: 'lobbyReverse', when: { [ZENITH_PARAM.isDying]: true }, fade: 0.5 }],
    randomLayers: SPEC_RANDOM,
    // f9 → f0：两个都不淡化；f9 先 @BAR 播收尾，收尾播完立刻满音量接 f0 的引入
    transitionIn: { at: 'bar', out: 'outro', in: 'after' },
  },

  /**
   * 第九层。**逆位是互斥变体** ——
   * 原版 `.rcd` 的 swapList 把 buffer 8 换成 26，是整轨替换。
   * 两个播放器同时起播、同步跑，切换只改增益，所以位置不丢。
   */
  final: {
    track: 'final',
    loop: FINAL_LOOP,
    playFrom: FINAL_INTRO,
    outro: FINAL_OUTRO,
    variant: {
      options: [
        { track: 'final', when: { [ZENITH_PARAM.isDying]: false } },
        { track: 'finalReverse', when: { [ZENITH_PARAM.isDying]: true } },
      ],
      fade: 0.5,
    },
    // f0 → f9：f0 立即淡出；淡出完毕后 f9 立刻满音量起，两边都不 @BAR
    transitionIn: { at: 'immediate', out: 'fade', outFade: 0.6, in: 'after' },
  },
};

// -------------------------------------------------------------- 规则 ----

/**
 * **按顺序求值，先匹配者胜**。
 *
 * 只有 `isFinal` 参与选曲 —— `isDying` 和 `energy` 都是「层」级别的参数，
 * 不会换 cue（这正是位置得以保持的原因）。
 */
const rules: readonly CueRule[] = [
  { when: { [ZENITH_PARAM.isFinal]: true }, cue: 'final' },
  { when: {}, cue: 'lobby' },
];

// -------------------------------------------------------------- 乐谱 ----

/**
 * 造一份 zenith 乐谱。
 *
 * ```ts
 * const bgm = new BgmDirector(createZenithScore(), zenithParams({ isFinal: false, isDying: false, energy: 3 }));
 * await bgm.load();          // 可以提前，不等用户手势
 * // 用户点「开始」时：
 * await bgm.start();
 * // 之后随时：
 * bgm.setParams(zenithParams({ isFinal: true, isDying: false, energy: 3 }));
 * ```
 */
export function createZenithScore(): BgmScore {
  return {
    tracks,
    cues,
    rules,
    defaultCue: 'lobby',
    // isDying 上升沿 → 下一小节把音乐拉回循环开头。**每个 isFinal 阶段只生效一次**
    restartOnRisingEdge: [ZENITH_PARAM.isDying],
    // isFinal 一变就重新武装上面那个闩锁（"重置次数"）
    clearRestartOnChange: [ZENITH_PARAM.isFinal],
    phraseBars: 2,
  };
}
